// frontend/src/widgets/AdvancedContactListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-09
// Updated:      2026-09-07 13:10 BST
//
// Description:
//   Advanced contact list with column chooser (portaled), drag/drop column
//   reordering, per-user/tenant persistence, expandable contact tools,
//   and clean API usage.
//
// Mobile Responsiveness (2026-09-07):
//   - Desktop table, drag/drop ordering, persistence, expansion and actions remain intact.
//   - Below 768px the SAME table rows reflow into labelled contact cards; no duplicate
//     mobile list is mounted and no duplicate contact/tool API work is introduced.
//   - Mobile cards contain every currently selected Column Chooser field, plus a
//     dedicated Contact Tools row and Edit action row.
//   - Mobile Sort By + direction controls replace hidden table-header sorting.
//   - Column Chooser adds touch-safe up/down ordering while desktop drag/drop remains.
//   - The first rendered contact card is measured and the fixed mobile page host is
//     expanded just enough to show one COMPLETE collapsed contact card per scroll.
//   - Collapsed cards use mandatory scroll snapping. When any contact tool tray is
//     expanded, snapping is disabled so Documents/Messages content can scroll freely.
//   - Expanded ContactDocumentsAccordian and ContactMessagesAccordian remain in their
//     original single render path directly beneath the owning contact.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { filterAndSortList, getWidgetTitle } from "../utils/widgetUtils";
import ModalWrapper from "../components/ModalWrapper";
import { useDashboardRefresh } from "../context/DashboardRefreshContext";
import ContactMessagesAccordian from "../components/ContactMessagesAccordian";
import ContactDocumentsAccordian from "../components/ContactDocumentsAccordian";

// ---------- Helpers ----------
const decodeJWT = (token) => {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return {};
  }
};

const getUserTenantKey = () => {
  const token =
    localStorage.getItem("anpr_token") || localStorage.getItem("token") || "";
  const p = decodeJWT(token) || {};
  const userId = p.sub || p.user_id || p.uid || "unknownUser";
  const tenantId = p.tenant_id || p.tid || "unknownTenant";
  return { userId, tenantId };
};

const storageKey = (userId, tenantId) =>
  `anpr_contact_columns_${tenantId}_${userId}`;

// Column registry (all contact fields including the Mon/Tue additions)
const ALL_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "title", label: "Title" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },

  // Employment
  { key: "department", label: "Department" },
  { key: "job_title", label: "Job Title" },

  // Emails
  { key: "email", label: "Email" },
  { key: "secondary_email", label: "Secondary Email" },

  // Phones
  { key: "phone_number", label: "Main Phone" },
  { key: "mobile_number", label: "Mobile" },
  { key: "direct_line", label: "Direct Line" },
  { key: "extension", label: "Ext." },
  { key: "whatsapp", label: "WhatsApp" },

  // Preferences
  { key: "preferred_contact_method", label: "Preferred Contact" },

  // Access & Roles
  { key: "status", label: "Status" },
  { key: "role", label: "Role" },
  { key: "access_level", label: "Access Level" },
  { key: "portal_access", label: "Portal Access" },

  // Flags
  { key: "is_primary", label: "Primary Contact" },
  { key: "is_billing_contact", label: "Billing Contact" },
  { key: "is_technical_contact", label: "Technical Contact" },

  // Associations
  { key: "customer_id", label: "Company (ID)" },

  // Notes
  { key: "notes", label: "Notes" },
];

// A sensible default set for contacts
const DEFAULT_VISIBLE_ORDER = [
  "first_name",
  "last_name",
  "email",
  "phone_number",
  "mobile_number",
  "status",
  "role",
];

// Fields to search across
const SEARCH_FIELDS = [
  "first_name",
  "last_name",
  "email",
  "secondary_email",
  "phone_number",
  "mobile_number",
  "direct_line",
  "whatsapp",
  "department",
  "job_title",
  "role",
  "access_level",
  "preferred_contact_method",
  "status",
  "notes",
];

// Small value accessor / formatting
const getCell = (row, key) => {
  const v = row?.[key];
  if (v === null || typeof v === "undefined") return "";

  // Bool niceties
  if (
    key === "portal_access" ||
    key === "is_primary" ||
    key === "is_billing_contact" ||
    key === "is_technical_contact"
  ) {
    return v ? "Yes" : "No";
  }

  return v;
};

// ---------- Column Chooser Modal (PORTALED) ----------
const ColumnChooserModal = ({
  open,
  onClose,
  allColumns,
  visibilityMap,
  setVisibilityMap,
  order,
  setOrder,
  resetToDefault,
}) => {
  // Body scroll lock while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const handleToggle = (key) => {
    setVisibilityMap((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const moveColumn = (key, direction) => {
    setOrder((prev) => {
      const fromIndex = prev.indexOf(key);
      const toIndex = fromIndex + direction;

      if (fromIndex < 0 || toIndex < 0 || toIndex >= prev.length) {
        return prev;
      }

      const next = prev.slice();
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const content = (
    <ModalWrapper onClose={onClose}>
      <h3 className="text-lg font-semibold mb-3">Choose Columns</h3>
      <p className="text-sm text-gray-600 mb-3">
        Tick the columns you want to see. Drag desktop table headers to reorder
        them, or use the mobile up/down controls below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-[60vh] md:max-h-80 overflow-auto pr-1 md:pr-2">
        {order.map((key, index) => {
          const col = allColumns.find((c) => c.key === key);
          if (!col) return null;

          return (
            <div
              key={key}
              className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-2 md:border-0 md:rounded-none md:p-0"
            >
              <label className="flex min-w-0 flex-1 items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={!!visibilityMap[key]}
                  onChange={() => handleToggle(key)}
                  className="h-5 w-5 shrink-0 md:h-auto md:w-auto"
                />
                <span className="truncate">{col.label}</span>
              </label>

              <div className="flex shrink-0 gap-1 md:hidden">
                <button
                  type="button"
                  onClick={() => moveColumn(key, -1)}
                  disabled={index === 0}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} up`}
                  title={`Move ${col.label} up`}
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => moveColumn(key, 1)}
                  disabled={index === order.length - 1}
                  className="min-h-[40px] min-w-[40px] rounded border border-gray-300 bg-white px-2 text-base font-semibold text-gray-700 disabled:opacity-35"
                  aria-label={`Move ${col.label} down`}
                  title={`Move ${col.label} down`}
                >
                  ↓
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <button
          onClick={resetToDefault}
          className="min-h-[44px] w-full bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Reset to Default
        </button>

        <button
          onClick={onClose}
          className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-semibold py-2 px-3 rounded shadow sm:w-auto"
        >
          Done
        </button>
      </div>
    </ModalWrapper>
  );

  const portalTarget = document.getElementById("modal-root") || document.body;
  return createPortal(content, portalTarget);
};

// ---------- Main Widget ----------
const AdvancedContactListWidget = ({
  onEdit,
  onNewEmail,
  onNewSms,
  onNewWhatsApp,
}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("first_name");
  const [ascending, setAscending] = useState(true);

  // Refresh support (updates after edits)
  const { refreshKey } = useDashboardRefresh();

  // Track expanded rows by contact id
  const [expanded, setExpanded] = useState(() => ({}));
  const toggleRow = (contactId) => {
    setExpanded((prev) => ({ ...prev, [contactId]: !prev[contactId] }));
  };

  // Column UI state
  const { userId, tenantId } = useMemo(getUserTenantKey, []);
  const persistKey = useMemo(
    () => storageKey(userId, tenantId),
    [userId, tenantId]
  );

  // Visibility map: { key: boolean }
  const [visibilityMap, setVisibilityMap] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return parsed.visibilityMap || {};
      } catch {}
    }
    const map = {};
    ALL_COLUMNS.forEach((c) => {
      map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key);
    });
    return map;
  });

  // Order array of keys
  const [order, setOrder] = useState(() => {
    const raw = localStorage.getItem(persistKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.order) && parsed.order.length)
          return parsed.order;
      } catch {}
    }
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
    return [...defaults, ...rest];
  });

  // Column chooser modal
  const [chooserOpen, setChooserOpen] = useState(false);

  // Mobile one-record viewport sizing. These refs measure the actual rendered
  // card rather than guessing from a fixed number of visible fields.
  const widgetRootRef = useRef(null);
  const mobileHeaderRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstCardRef = useRef(null);

  // Persist settings whenever they change
  useEffect(() => {
    const data = { order, visibilityMap, v: 1 };
    localStorage.setItem(persistKey, JSON.stringify(data));
  }, [order, visibilityMap, persistKey]);

  // Data fetch (clean path — no trailing slash)
  useEffect(() => {
    setLoading(true);
    axiosInstance
      .get("contacts?limit=1000")
      .then((res) => setContacts(res.data || []))
      .catch((err) =>
        console.error(
          "❌ Failed to fetch contacts:",
          err?.response?.data || err?.message || err
        )
      )
      .finally(() => setLoading(false));
  }, [refreshKey]);

  // Sorting
  const handleSort = (field) => {
    if (field === sortField) {
      setAscending((a) => !a);
    } else {
      setSortField(field);
      setAscending(true);
    }
  };

  // Drag & Drop for headers
  const dragIndexRef = useRef(null);

  const visibleKeys = useMemo(
    () => order.filter((k) => visibilityMap[k]),
    [order, visibilityMap]
  );

  const onHeaderDragStart = (e, key) => {
    const idx = visibleKeys.indexOf(key);
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
  };

  const onHeaderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onHeaderDrop = (e, dropKey) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    const toIdx = visibleKeys.indexOf(dropKey);
    if (fromIdx === null || fromIdx === -1 || toIdx === -1 || fromIdx === toIdx)
      return;

    // Reorder only visible keys; hidden keys keep their relative positions
    const newVisible = visibleKeys.slice();
    const [moved] = newVisible.splice(fromIdx, 1);
    newVisible.splice(toIdx, 0, moved);

    const hidden = order.filter((k) => !visibilityMap[k]);
    setOrder([...newVisible, ...hidden]);

    dragIndexRef.current = null;
  };

  const filteredContacts = useMemo(() => {
    return filterAndSortList(
      contacts,
      searchQuery,
      SEARCH_FIELDS,
      sortField,
      ascending
    );
  }, [contacts, searchQuery, sortField, ascending]);

  const anyContactToolsExpanded = useMemo(
    () => Object.values(expanded).some(Boolean),
    [expanded]
  );

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const root = widgetRootRef.current;
    const header = mobileHeaderRef.current;
    const viewport = recordsViewportRef.current;
    if (!root || !header || !viewport) return undefined;

    const mediaQuery = window.matchMedia("(max-width: 767px)");
    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;
      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];
      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();
        const widthMatches = Math.abs(rect.width - rootRect.width) < 90;
        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) return;

      animationFrame = requestAnimationFrame(() => {
        const firstCard = firstCardRef.current;
        const cardHeight = firstCard
          ? Math.ceil(firstCard.getBoundingClientRect().height)
          : 120;

        // tbody has 12px mobile padding above and below the first card.
        const recordViewportHeight = cardHeight + 24;
        viewport.style.height = `${recordViewportHeight}px`;
        viewport.style.flex = "0 0 auto";

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();
          if (!host) return;

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();
          const wrapperChromeHeight = Math.max(0, rootRect.top - hostRect.top);
          const requiredHostHeight = Math.ceil(
            Math.max(host.scrollHeight, wrapperChromeHeight + rootRect.height + 8)
          );

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver) {
      resizeObserver.observe(header);
      if (firstCardRef.current) resizeObserver.observe(firstCardRef.current);
    }

    const handleMediaChange = () => applyMobileSizing();
    mediaQuery.addEventListener?.("change", handleMediaChange);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      mediaQuery.removeEventListener?.("change", handleMediaChange);
      restoreHost();
      viewport.style.height = "";
      viewport.style.flex = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [filteredContacts, visibleKeys]);

  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return ascending ? " ▲" : " ▼";
  };

  const resetToDefault = () => {
    const map = {};
    ALL_COLUMNS.forEach((c) => (map[c.key] = DEFAULT_VISIBLE_ORDER.includes(c.key)));
    setVisibilityMap(map);
    const defaults = DEFAULT_VISIBLE_ORDER.slice();
    const rest = ALL_COLUMNS.map((c) => c.key).filter((k) => !defaults.includes(k));
    setOrder([...defaults, ...rest]);
    setSortField("first_name");
    setAscending(true);
  };

  // Expander cell (leftmost "+")
  const renderExpanderCell = (contact) => {
    const isOpen = !!expanded[contact.id];

    const baseStyle = {
      color: "var(--accent-color, #ff7a00)",
      cursor: "pointer",
      opacity: 1,
      width: 28,
      height: 28,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      borderRadius: 6,
      border: "1px solid rgba(0, 0, 0, 0.1)",
      background: "rgba(0,0,0,0.02)",
      userSelect: "none",
    };

    return (
      <button
        type="button"
        aria-label={isOpen ? "Collapse contact tools" : "Expand contact tools"}
        title={isOpen ? "Hide contact tools" : "Show contact tools"}
        onClick={() => toggleRow(contact.id)}
        style={baseStyle}
      >
        {isOpen ? "−" : "+"}
      </button>
    );
  };

  // Contact Function Tray (per contact row)
  const renderContactFunctionTray = (contact) => {
    const fullName =
      [contact.first_name, contact.last_name].filter(Boolean).join(" ").trim() ||
      `Contact #${contact.id}`;

    return (
      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
        <div className="text-sm font-semibold text-gray-800">
          Contact tools for {fullName} (ID #{contact.id})
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Contact Documents / Files accordion (safe stub, no backend assumptions yet) */}
          <ContactDocumentsAccordian contactId={contact.id} />

          {/* Contact Messages accordion (wired like JobMessagesAccordian but via contact_id) */}
          <ContactMessagesAccordian
            contactId={contact.id}
            defaultOpen={false}
            onNewEmail={onNewEmail}
            onNewSms={onNewSms}
            onNewWhatsApp={onNewWhatsApp}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-hidden rounded-lg border border-gray-200 shadow-sm bg-white flex flex-col md:h-full"
    >
      <div
        ref={mobileHeaderRef}
        className="bg-accent text-white px-3 py-3 md:px-4 md:py-2 flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-between md:items-center"
      >
        <span className="text-sm font-semibold">
          Search the {getWidgetTitle("advanced_contact_list")}
        </span>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:flex md:w-auto md:items-center">
          <input
            type="text"
            placeholder="Search..."
            className="min-h-[44px] min-w-0 w-full rounded border border-white bg-white px-3 py-2 text-sm text-black md:min-h-0 md:w-48 md:flex-none md:px-2 md:py-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button
            type="button"
            onClick={() => setChooserOpen(true)}
            className="min-h-[44px] whitespace-nowrap bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white text-xs font-semibold px-3 py-2 rounded shadow md:min-h-0 md:py-1"
            title="Choose Columns"
          >
            Column Chooser
          </button>
        </div>

        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-2 md:hidden">
          <label className="flex min-w-0 flex-col gap-1 text-xs font-semibold">
            Sort by
            <select
              value={sortField}
              onChange={(event) => setSortField(event.target.value)}
              className="min-h-[44px] w-full rounded border border-white bg-white px-3 py-2 text-sm font-normal text-black"
            >
              {ALL_COLUMNS.map((column) => (
                <option key={column.key} value={column.key}>
                  {column.label}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            onClick={() => setAscending((current) => !current)}
            className="min-h-[44px] self-end rounded bg-btnGeneralNormal px-4 py-2 text-sm font-semibold text-white shadow hover:bg-btnGeneralHover active:bg-btnGeneralClick"
            aria-label={ascending ? "Sort descending" : "Sort ascending"}
            title={
              ascending
                ? "Currently ascending — tap for descending"
                : "Currently descending — tap for ascending"
            }
          >
            {ascending ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div
        ref={recordsViewportRef}
        className={`min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain scroll-smooth md:flex-1 md:overflow-auto ${
          anyContactToolsExpanded
            ? "snap-none"
            : "snap-y snap-mandatory md:snap-none"
        }`}
      >
        <table className="block w-full text-left text-sm text-gray-700 md:table">
          <thead className="hidden md:table-header-group">
            <tr className="bg-accent text-white select-none">
              {/* Expander header (sticky) */}
              <th className="px-2 py-2 text-center w-10 sticky top-0 z-10 bg-accent">
                +
              </th>

              {visibleKeys.map((key) => {
                const col = ALL_COLUMNS.find((c) => c.key === key);
                if (!col) return null;

                return (
                  <th
                    key={key}
                    className="px-4 py-2 cursor-move sticky top-0 z-10 bg-accent"
                    draggable
                    onDragStart={(e) => onHeaderDragStart(e, key)}
                    onDragOver={onHeaderDragOver}
                    onDrop={(e) => onHeaderDrop(e, key)}
                    onClick={() => handleSort(key)}
                    title="Drag to reorder • Click to sort"
                  >
                    {col.label}
                    {renderSortIndicator(key)}
                  </th>
                );
              })}

              {/* Actions column fixed at the end (not draggable, sticky) */}
              <th className="px-4 py-2 text-center sticky top-0 z-10 bg-accent">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="block space-y-3 p-3 md:table-row-group md:space-y-0 md:p-0">
            {loading ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 2}
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  Loading contacts...
                </td>
              </tr>
            ) : filteredContacts.length === 0 ? (
              <tr className="block md:table-row">
                <td
                  colSpan={visibleKeys.length + 2}
                  className="block rounded-lg border border-gray-200 bg-white px-4 py-5 text-center text-gray-500 italic md:table-cell md:rounded-none md:border-0"
                >
                  No contacts found.
                </td>
              </tr>
            ) : (
              filteredContacts.map((c, index) => {
                const isOpen = !!expanded[c.id];

                return (
                  <React.Fragment key={c.id}>
                    <tr
                      ref={index === 0 ? firstCardRef : undefined}
                      className="block snap-start scroll-mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm md:table-row md:rounded-none md:border-x-0 md:border-t-0 md:border-b md:border-gray-100 md:shadow-none md:align-top md:odd:bg-gray-50 md:even:bg-white"
                    >
                      {/* Expander / tools row */}
                      <td className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-2 md:py-2 md:text-center md:align-middle">
                        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                          Contact Tools
                        </span>
                        {renderExpanderCell(c)}
                      </td>

                      {/* Data cells */}
                      {visibleKeys.map((key) => {
                        const col = ALL_COLUMNS.find((column) => column.key === key);
                        if (!col) return null;

                        return (
                          <td
                            key={key}
                            className="flex items-start justify-between gap-4 border-b border-gray-100 px-4 py-3 md:table-cell md:border-b-0 md:px-4 md:py-2"
                          >
                            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500 md:hidden">
                              {col.label}
                            </span>
                            <span className="min-w-0 break-words text-right md:text-left">
                              {getCell(c, key)}
                            </span>
                          </td>
                        );
                      })}

                      {/* Actions */}
                      <td className="block px-4 py-3 md:table-cell md:px-4 md:py-2 md:text-center">
                        <button
                          type="button"
                          onClick={() => onEdit?.(c)}
                          className="min-h-[44px] w-full bg-btnActionNormal hover:bg-btnActionHover active:bg-btnActionClick text-white text-sm font-body px-3 py-2 rounded shadow transition-colors duration-200 ease-in-out md:min-h-0 md:w-auto md:text-xs md:py-1"
                          title="Edit Contact"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>

                    {/* Contact Function Tray row */}
                    {isOpen && (
                      <tr className="block bg-white md:table-row">
                        <td
                          colSpan={visibleKeys.length + 2}
                          className="block rounded-lg border border-gray-200 bg-white p-3 shadow-sm md:table-cell md:rounded-none md:border-0 md:px-4 md:pb-4 md:pt-0 md:shadow-none"
                        >
                          {renderContactFunctionTray(c)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <ColumnChooserModal
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        allColumns={ALL_COLUMNS}
        visibilityMap={visibilityMap}
        setVisibilityMap={setVisibilityMap}
        order={order}
        setOrder={setOrder}
        resetToDefault={resetToDefault}
      />
    </div>
  );
};

export default AdvancedContactListWidget;
